import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {FormInput, NumberInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ActionWithReasonButton} from 'src/components/save-with-reason/action-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';
import {LocaleContext} from '@pb/kit-context-locale';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {GrantResources} from '../grant-resources/grant-resources';
import {useAssignResourcesToGrant} from '../grant-resources/useAssignResourcesToGrant';
import {getCleanedExternalId, getEntitlementsReturnPath, validateExternalId} from '../utils';

import {
    GRANT_DESCRIPTION_CONTROL,
    GRANT_EXTERNAL_ID_CONTROL,
    GRANT_NAME_CONTROL,
} from './constants';
import {useCreateGrant} from './useCreateGrant';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {CheckBoxChangeData} from 'src/typedef';


const CreateGrant = () => {
    const navigate = useNavigate();
    const {state: navigationState} = useLocation();
    const {locale} = useContext(LocaleContext);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {createGrantOpen, createGrantSubmit} = useEventTracking();
    const {proceed, setIsChunkChanged} = useConfirmationChunk({chunkName: 'create-grant', onProceed: () => {
        setSuccessMessage('Grant has been successfully created.');
        navigate(getEntitlementsReturnPath(navigationState));
    }});

    const [busy, setBusy] = useState(false);
    const [createdExternalId, setCreatedExternalId] = useState<string>();
    const metadataDisabled = busy || !!createdExternalId;

    const {
        data,
        setData,
        isChanged: isGrantChanged,
        saveChanges: createGrant,
        validateChanges: validateGrantFields,
    } = useCreateGrant();

    const {
        data: resourceIds,
        setData: setResourceIds,
        isChanged: isResourcesChanged,
        saveChanges: assignResources,
    } = useAssignResourcesToGrant();

    useEffect(() => {
        createGrantOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsChunkChanged(isGrantChanged || isResourcesChanged);
    }, [isGrantChanged, isResourcesChanged, setIsChunkChanged]);

    const changeExternalId = useCallback((id: string) => {
        let validId = id.toLowerCase().replace(/\s+/g, '-');
        if (validId && !validId.startsWith('grant.')) {
            validId = `grant.${id}`;
        }
        setData(prev => ({...prev, id: validId}));
    }, [setData]);

    const changeName = useCallback((name: string) => {
        setData(prev => ({
            ...prev,
            name,
            id: (name && !prev.id)
                ? getCleanedExternalId(name, 'grant')
                : prev.id,
        }));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const setManageableGrant = useCallback((changeData: CheckBoxChangeData) => {
        setData(prev => ({...prev, requiredApprovals: changeData.event.target.checked}));
    }, [setData]);

    const blurDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const changeStatus = useCallback((status: GrantStatus) => {
        setData(prev => ({...prev, status}));
    }, [setData]);

    const changeDaysToDelay = useCallback((daysToDelay?: number) => {
        setData(prev => ({...prev, daysToDelay}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        navigate(-1);
        createGrantSubmit({submit: false, success: true});
    }, [createGrantSubmit, navigate]);

    const createGrantHandler = useCallback(async (options?: ActionOptions) => {
        if (validateGrantFields()) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            createGrantSubmit({submit: true, success: false});
            return BASIC_VALIDATION_FAILED;
        }

        setBusy(true);
        try {
            const result = createdExternalId ? {externalId: createdExternalId} : await createGrant();
            if (typeof result === 'object' && result.externalId) {
                setCreatedExternalId(result.externalId);
                const assignError = await assignResources({grantId: result.externalId, ...options});
                if (assignError) {
                    setErrorMessage(assignError);
                } else {
                    proceed();
                }
                createGrantSubmit({submit: true, success: !assignError});
                return assignError;
            } else if (result) {
                createGrantSubmit({submit: true, success: false});
                setErrorMessage(result as string);
                return result as string;
            }
        } finally {
            setBusy(false);
        }
    }, [
        createdExternalId, createGrant, assignResources, createGrantSubmit,
        proceed, setErrorMessage, validateGrantFields,
    ]);

    const addResources = useCallback((resourceId: string) => {
        setResourceIds(prev => [...new Set([...prev, resourceId])]);
    }, [setResourceIds]);

    const removeResources = useCallback((resourceId: string) => {
        setResourceIds(prev => prev.filter(id => id !== resourceId));
    }, [setResourceIds]);

    return (
        <ServerResponseHandler
            serverError={null}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName="Create Grant">
            <ManageEntitlementsPage
                title="Create Grant"
                buttonsBlock={(
                    <Group>
                        <ActionWithReasonButton action="Create" legacyLabel="Create" busy={busy} onConfirm={createGrantHandler}/>
                        <Button variant="Type.SECONDARY" disabled={busy} onClick={backToAllEntitlements}>
                            Cancel
                        </Button>
                    </Group>
                )}>
                <Item className="group-item pd-l">
                    {createdExternalId ? (
                        <Message color="info">
                            Grant information has been saved. Retry assigning resources to complete creation.
                        </Message>
                    ) : null}
                    <FormSection headline="Grant Information">
                        <Group direction="v">
                            <CustomValidatedControl name={GRANT_NAME_CONTROL}>
                                {error => (
                                    <FormInput
                                        disabled={metadataDisabled}
                                        value={data.name}
                                        width={300}
                                        maxLength={64}
                                        label={<RequiredMark label="Grant Name"/>}
                                        placeholder="Grant Name"
                                        onBlur={changeName}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <CustomValidatedControl name={GRANT_EXTERNAL_ID_CONTROL}>
                                {error => (
                                    <FormInput
                                        disabled={metadataDisabled}
                                        value={data.id}
                                        width={300}
                                        maxLength={64}
                                        validation={id => validateExternalId(id, 'grant')}
                                        label={<RequiredMark label="Grant External ID"/>}
                                        placeholder="Grant External ID"
                                        onBlur={changeExternalId}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <ValidatedControl name={GRANT_DESCRIPTION_CONTROL}>
                                {invalid => (
                                    <WidthItem width={610}>
                                        <Label><RequiredMark label="Description"/></Label>
                                        <Textarea
                                            disabled={metadataDisabled}
                                            maxLength={255}
                                            color={invalid ? 'ERROR' : 'DEFAULT'}
                                            value={data.description}
                                            onChange={changeDescription}
                                            onBlur={blurDescription}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                            <CheckBox
                                disabled={metadataDisabled}
                                label="Manageable Grant"
                                checked={data.requiredApprovals}
                                onCheckBoxChange={setManageableGrant}
                            />
                        </Group>
                    </FormSection>
                    <FormSection headline="Permission">
                        <Group gap="l">
                            <WidthItem width={290}>
                                <Label
                                    id="grant-status-select-label"
                                    htmlFor="grant-status-select">
                                    Status
                                </Label>
                                <ComboBox
                                    disabled={metadataDisabled}
                                    id="grant-status-select"
                                    aria-labelledby="grant-status-select-label"
                                    placeholder="Select Status"
                                    value={data.status}
                                    onChange={changeStatus}>
                                    <ComboboxItem value={GrantStatus.ALLOW} caption="Allow"/>
                                    <ComboboxItem value={GrantStatus.DENY} caption="Deny"/>
                                </ComboBox>
                            </WidthItem>
                            <WidthItem width={300}>
                                <Label>Restrictions</Label>
                                <CustomGroupItem label="Days to Delay">
                                    <NumberInput
                                        disabled={metadataDisabled}
                                        value={data.daysToDelay}
                                        displayedValue={data.daysToDelay !== undefined
                                            ? Integer.format(data.daysToDelay, locale)
                                            : 'Enter Day(s)'}
                                        onBlur={changeDaysToDelay}
                                        width={180}
                                        maxValue={9999}
                                    />
                                </CustomGroupItem>
                            </WidthItem>
                        </Group>
                    </FormSection>
                    <GrantResources
                        resourceIds={resourceIds}
                        onAddResource={addResources}
                        onRemoveResource={removeResources}
                    />
                </Item>
            </ManageEntitlementsPage>
        </ServerResponseHandler>
    );
};

export const ValidateCreateGrant = () => (
    <ValidationContextProvider>
        <CreateGrant/>
    </ValidationContextProvider>
);
